import { StoreInfo } from '@/types/store';
import { CartItem } from '@/types';

export interface ShipbubbleShippingRequest {
  storeId: number;
  destination: {
    address: string;
    city: string;
    state: string;
    country: string;
  };
  weight: number;
  items: Array<{
    name: string;
    quantity: number;
    weight: number;
    value: number;
  }>;
}

export interface ShipbubbleShippingResponse {
  status: boolean;
  message: string;
  data?: {
    cost: number;
    currency: string;
    estimated_delivery: string;
    service_type: string;
    tracking_available: boolean;
  };
}

export interface ShippingOption {
  id: string;
  name: string;
  cost: number;
  currency: string;
  estimated_delivery: string;
  service_type: string;
  tracking_available: boolean;
}

/**
 * Check if Shipbubble is enabled and configured for the store
 */
export function isShipbubbleEnabled(storeInfo: StoreInfo | null): boolean {
  return !!(storeInfo?.shipbubble && storeInfo?.shipbuKeybble);
}

/**
 * Calculate shipping cost using Shipbubble API
 */
export async function calculateShipbubbleShipping(
  storeInfo: StoreInfo | null,
  request: ShipbubbleShippingRequest
): Promise<ShipbubbleShippingResponse> {
  if (!isShipbubbleEnabled(storeInfo)) {
    return {
      status: false,
      message: 'Shipbubble is not configured for this store'
    };
  }

  try {
    const response = await fetch('https://api.cultaz.com/API/shipbubbleShipping.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Shipbubble API error:', error);
    return {
      status: false,
      message: 'Failed to calculate shipping cost'
    };
  }
}

/**
 * Convert cart items to Shipbubble format
 */
export function convertCartItemsToShipbubble(cartItems: CartItem[]): Array<{
  name: string;
  quantity: number;
  weight: number;
  value: number;
}> {
  return cartItems.map(item => ({
    name: item.produt_name,
    quantity: item.quantity,
    weight: 0.5, // Default weight in kg, could be made configurable
    value: parseFloat(item.sellingprice) * item.quantity
  }));
}

/**
 * Calculate total weight of cart items
 */
export function calculateCartWeight(cartItems: CartItem[]): number {
  return cartItems.reduce((total, item) => {
    return total + (0.5 * item.quantity); // Default 0.5kg per item
  }, 0);
}

/**
 * Get shipping options with fallback to manual shipping
 */
export async function getShippingOptions(
  storeInfo: StoreInfo | null,
  cartItems: CartItem[],
  destination: {
    address: string;
    city: string;
    state: string;
    country: string;
  },
  manualShippingCost: number = 0
): Promise<{
  options: ShippingOption[];
  defaultOption: string;
}> {
  const options: ShippingOption[] = [];
  
  // Add manual shipping option
  if (manualShippingCost > 0) {
    options.push({
      id: 'manual',
      name: 'Standard Shipping',
      cost: manualShippingCost,
      currency: storeInfo?.currenCy || 'NGN',
      estimated_delivery: '3-5 business days',
      service_type: 'standard',
      tracking_available: false
    });
  }
  
  // Add Shipbubble options if enabled
  if (isShipbubbleEnabled(storeInfo)) {
    try {
      const shipbubbleRequest: ShipbubbleShippingRequest = {
        storeId: storeInfo?.id || 0,
        destination,
        weight: calculateCartWeight(cartItems),
        items: convertCartItemsToShipbubble(cartItems)
      };
      
      const shipbubbleResponse = await calculateShipbubbleShipping(storeInfo, shipbubbleRequest);
      
      if (shipbubbleResponse.status && shipbubbleResponse.data) {
        options.push({
          id: 'shipbubble',
          name: 'Shipbubble Delivery',
          cost: shipbubbleResponse.data.cost,
          currency: shipbubbleResponse.data.currency,
          estimated_delivery: shipbubbleResponse.data.estimated_delivery,
          service_type: shipbubbleResponse.data.service_type,
          tracking_available: shipbubbleResponse.data.tracking_available
        });
      }
    } catch (error) {
      console.error('Failed to get Shipbubble options:', error);
    }
  }
  
  // Determine default option
  const defaultOption = options.length > 0 ? options[0].id : 'manual';
  
  return {
    options,
    defaultOption
  };
}
